import { useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { Button, Field, PasswordField, SelectField, StatusBadge } from "@fawkes/ui";

// jve-settings-boekhouding: the React port of the Jmix `buildFinanceConnectionCard()` — the
// "Koppeling boekhouding" (accounting connection) box of the Financieel tile. A provider selector
// over the four accounting packages (Exact Online, Twinfield, SnelStart, AFAS) plus a status box
// with connect / settings / disconnect / resync, all wired to `/api/boekhouding/connections`.
//
// Secret policy: the OAuth client secret is write-only. The GET never returns it (only a
// `clientSecretConfigured` flag), and the connect/settings form leaves the PasswordField blank —
// submitting blank keeps the stored secret server-side.

export interface BoekhoudingConnection {
  provider: string;
  displayName: string;
  extraLabel: string;
  configured: boolean;
  connected: boolean;
  accountName: string | null;
  connectedAt: string | null;
  clientId: string | null;
  redirectUri: string | null;
  extra: string | null;
  clientSecretConfigured: boolean;
}

// The provider-specific extra identifier (division/office/administration/environment) → i18n label.
const EXTRA_LABEL_KEYS: Record<string, string> = {
  division: "instellingen.boekhouding.extra.division",
  officeCode: "instellingen.boekhouding.extra.officeCode",
  administrationId: "instellingen.boekhouding.extra.administrationId",
  environmentId: "instellingen.boekhouding.extra.environmentId",
};

type FormMode = "connect" | "settings" | null;

interface FormState {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  extra: string;
}

const EMPTY_FORM: FormState = { clientId: "", clientSecret: "", redirectUri: "", extra: "" };

export function FinanceConnectionBox() {
  const t = useTranslate();
  const [selectedProvider, setSelectedProvider] = useState("exact-online");
  const [formMode, setFormMode] = useState<FormMode>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const { data, refetch, isLoading } = useCustom<BoekhoudingConnection[]>({
    url: "boekhouding/connections",
    method: "get",
  });
  const { mutate, isLoading: isMutating } = useCustomMutation();

  const connections = data?.data ?? [];
  const active = connections.find((c) => c.provider === selectedProvider) ?? connections[0];

  function extraLabel(conn: BoekhoudingConnection | undefined): string {
    const key = conn ? EXTRA_LABEL_KEYS[conn.extraLabel] : undefined;
    return key ? t(key) : conn?.extraLabel ?? "";
  }

  function formatLastSync(connectedAt: string | null | undefined): string {
    if (!connectedAt) return t("instellingen.boekhouding.lastSync.never");
    const parsed = new Date(connectedAt);
    return Number.isNaN(parsed.getTime()) ? connectedAt : parsed.toLocaleString();
  }

  function openForm(mode: Exclude<FormMode, null>) {
    setErrorKey(null);
    setForm({
      clientId: active?.clientId ?? "",
      clientSecret: "",
      redirectUri: active?.redirectUri ?? "",
      extra: active?.extra ?? "",
    });
    setFormMode(mode);
  }

  function submitForm() {
    if (!active) return;
    setErrorKey(null);
    const connecting = formMode === "connect";
    mutate(
      {
        // Relative path: the dataProvider prepends "/api"; an absolute path would double it.
        url: `boekhouding/connections/${active.provider}/${connecting ? "connect" : "settings"}`,
        method: connecting ? "post" : "put",
        values: {
          clientId: form.clientId,
          clientSecret: form.clientSecret,
          redirectUri: form.redirectUri,
          extra: form.extra,
        },
      },
      {
        onSuccess: () => {
          setFormMode(null);
          setForm(EMPTY_FORM);
          refetch();
        },
        onError: (error) => {
          const status = (error as { statusCode?: number })?.statusCode;
          setErrorKey(
            status === 400
              ? "instellingen.boekhouding.error.connectRequired"
              : "instellingen.boekhouding.error.generic",
          );
        },
      },
    );
  }

  function runAction(action: "resync" | "disconnect") {
    if (!active) return;
    setErrorKey(null);
    mutate(
      {
        url: `boekhouding/connections/${active.provider}/${action}`,
        method: "post",
        values: {},
      },
      {
        onSuccess: () => refetch(),
        onError: () => setErrorKey("instellingen.boekhouding.error.generic"),
      },
    );
  }

  const connected = active?.connected ?? false;

  return (
    <div className="settings-finance-connection-card">
      <h4 className="settings-finance-card-title">{t("instellingen.boekhouding.title")}</h4>
      <span className="settings-finance-card-subtitle">{t("instellingen.boekhouding.subtitle")}</span>

      <SelectField
        label={t("instellingen.boekhouding.providerLabel")}
        className="settings-finance-provider-select"
        value={selectedProvider}
        onChange={(e) => {
          setSelectedProvider(e.target.value);
          setFormMode(null);
          setErrorKey(null);
        }}
        options={connections.map((c) => ({ value: c.provider, label: c.displayName }))}
      />

      <Button
        variant="primary"
        className="settings-finance-connect-button"
        disabled={isLoading || isMutating || !active}
        onClick={() => openForm("connect")}
      >
        {t("instellingen.boekhouding.action.connect")}
      </Button>

      <div className="settings-finance-connection-box">
        <div className="settings-finance-connection-status-row">
          <StatusBadge tone={connected ? "success" : "neutral"}>
            {t(
              connected
                ? "instellingen.boekhouding.badge.connected"
                : "instellingen.boekhouding.badge.disconnected",
            )}
          </StatusBadge>
          <span className="settings-finance-connection-status-text">
            {t(
              connected
                ? "instellingen.boekhouding.status.connected"
                : "instellingen.boekhouding.status.disconnected",
              { args: [active?.displayName ?? ""] },
            )}
          </span>
        </div>

        <span className="settings-finance-connection-subtext">
          {connected && active?.accountName
            ? t("instellingen.boekhouding.account", { args: [active.accountName] })
            : t("instellingen.boekhouding.account.none")}
        </span>
        <span className="settings-finance-connection-subtext">
          {t("instellingen.boekhouding.lastSync", { args: [formatLastSync(active?.connectedAt)] })}
        </span>

        <Button
          variant="primary"
          className="settings-finance-resync-button"
          disabled={!connected || isMutating}
          onClick={() => runAction("resync")}
        >
          {t("instellingen.boekhouding.action.resync")}
        </Button>

        <div className="settings-finance-connection-actions">
          <Button
            variant="secondary"
            className="settings-finance-connection-settings"
            disabled={isMutating || !active}
            onClick={() => openForm("settings")}
          >
            {t("instellingen.boekhouding.action.settings")}
          </Button>
          <Button
            variant="secondary"
            className="settings-finance-connection-disconnect"
            disabled={!connected || isMutating}
            onClick={() => runAction("disconnect")}
          >
            {t("instellingen.boekhouding.action.disconnect")}
          </Button>
        </div>
      </div>

      {formMode && active && (
        <form
          className="settings-finance-connection-form"
          onSubmit={(e) => {
            e.preventDefault();
            submitForm();
          }}
        >
          <p className="settings-finance-connection-form-title">
            {t(
              formMode === "connect"
                ? "instellingen.boekhouding.form.connectTitle"
                : "instellingen.boekhouding.form.settingsTitle",
              { args: [active.displayName] },
            )}
          </p>
          <Field
            label={t("instellingen.boekhouding.form.clientId")}
            value={form.clientId}
            onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))}
          />
          <PasswordField
            label={t("instellingen.boekhouding.form.clientSecret")}
            placeholder={
              active.clientSecretConfigured ? t("instellingen.boekhouding.secret.configured") : undefined
            }
            value={form.clientSecret}
            onChange={(e) => setForm((f) => ({ ...f, clientSecret: e.target.value }))}
          />
          <span className="settings-finance-connection-form-hint">
            {t("instellingen.boekhouding.form.clientSecret.keep")}
          </span>
          <Field
            label={t("instellingen.boekhouding.form.redirectUri")}
            value={form.redirectUri}
            onChange={(e) => setForm((f) => ({ ...f, redirectUri: e.target.value }))}
          />
          <Field
            label={extraLabel(active)}
            value={form.extra}
            onChange={(e) => setForm((f) => ({ ...f, extra: e.target.value }))}
          />
          {errorKey && <p className="settings-finance-connection-form-error">{t(errorKey)}</p>}
          <div className="settings-finance-connection-form-actions">
            <Button type="submit" variant="primary" disabled={isMutating}>
              {t("instellingen.boekhouding.save")}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setFormMode(null)}>
              {t("common.cancel")}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
